import { createContext, useContext, useState, useEffect } from 'react';
import { customerAuthAPI, cartAPI } from '../services/api';

const CustomerAuthContext = createContext(null);

// Generate cryptographically secure session ID for guest cart
const getSessionId = () => {
    let sessionId = localStorage.getItem('noora_session_id');
    if (!sessionId) {
        const arr = new Uint8Array(24);
        crypto.getRandomValues(arr);
        const hex = Array.from(arr, b => b.toString(16).padStart(2, '0')).join('');
        sessionId = 'sess_' + hex;
        localStorage.setItem('noora_session_id', sessionId);
    }
    return sessionId;
};

export const CustomerAuthProvider = ({ children }) => {
    const [customer, setCustomer] = useState(null);
    const [profileIncomplete, setProfileIncomplete] = useState(false);
    const [loading, setLoading] = useState(true);

    // Ensure session ID exists
    getSessionId();

    useEffect(() => {
        const token = localStorage.getItem('noora_customer_token');
        const savedCustomer = localStorage.getItem('noora_customer');
        if (token && savedCustomer) {
            setCustomer(JSON.parse(savedCustomer));
            customerAuthAPI.me().then(res => {
                setCustomer(res.data.customer);
                setProfileIncomplete(!!res.data.profileIncomplete);
                localStorage.setItem('noora_customer', JSON.stringify(res.data.customer));
            }).catch(() => {
                localStorage.removeItem('noora_customer_token');
                localStorage.removeItem('noora_customer');
                setCustomer(null);
            });
        }
        setLoading(false);
    }, []);

    const login = async (email, password) => {
        const res = await customerAuthAPI.login({ email, password });
        localStorage.setItem('noora_customer_token', res.data.token);
        localStorage.setItem('noora_customer', JSON.stringify(res.data.customer));
        setCustomer(res.data.customer);
        // Merge guest cart
        try { await cartAPI.merge(); } catch (e) { /* ignore */ }
        return res.data;
    };

    const register = async (data) => {
        const res = await customerAuthAPI.register(data);
        localStorage.setItem('noora_customer_token', res.data.token);
        localStorage.setItem('noora_customer', JSON.stringify(res.data.customer));
        setCustomer(res.data.customer);
        try { await cartAPI.merge(); } catch (e) { /* ignore */ }
        return res.data;
    };

    const googleLogin = async (credential) => {
        const res = await customerAuthAPI.googleLogin({ credential });
        localStorage.setItem('noora_customer_token', res.data.token);
        localStorage.setItem('noora_customer', JSON.stringify(res.data.customer));
        setCustomer(res.data.customer);
        setProfileIncomplete(!!res.data.profileIncomplete);
        try { await cartAPI.merge(); } catch (e) { /* ignore */ }
        return res.data;
    };

    const logout = () => {
        localStorage.removeItem('noora_customer_token');
        localStorage.removeItem('noora_customer');
        setCustomer(null);
    };

    const updateCustomer = (data) => {
        setCustomer(data);
        setProfileIncomplete(!data.phone);
        localStorage.setItem('noora_customer', JSON.stringify(data));
    };

    return (
        <CustomerAuthContext.Provider value={{ customer, login, register, googleLogin, logout, loading, updateCustomer, profileIncomplete }}>
            {children}
        </CustomerAuthContext.Provider>
    );
};

const defaultCustomerAuth = {
    customer: null, login: () => {}, register: () => {}, googleLogin: () => {},
    logout: () => {}, loading: false, updateCustomer: () => {}, profileIncomplete: false
};
export const useCustomerAuth = () => useContext(CustomerAuthContext) || defaultCustomerAuth;
